import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    GeofenceSpeedLimit,
    VehicleSettingsContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { Vehicle } from '@polaris-customerweb/sharedcomponents.garage';
import {
    Geofence,
    ShowOnMapService,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { cloneDeep, every, filter, remove } from 'lodash-es';
import { Observable, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import {
    CreateGeofence,
    DeleteGeofence,
    ForceGeofenceRefresh,
    SetGeofences,
    UpdateGeofence,
} from 'src/app/components/geofence/geofence.actions';
import {
    selectGeofences,
    selectShouldForceGeofenceRefresh,
} from 'src/app/components/geofence/geofence.reducer';
import { AppState } from 'src/app/models/app-state';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { UserAuthService } from '../state/user-auth/user-auth.service';
import { WebVehicleService } from '../state/vehicle/vehicle.service';
import { GeofenceResourceService } from './geofence-resource.service';

@Injectable({ providedIn: 'root' })
export class GeofenceService {
    constructor(
        private geofenceResourceService: GeofenceResourceService,
        private showOnMapService: ShowOnMapService,
        private store: Store<AppState>,
        private userAuthService: UserAuthService,
        private webVehicleService: WebVehicleService
    ) {}

    //#region Get
    getGeofences$(): Observable<Geofence[]> {
        return combineLatest([
            this.store.select(selectShouldForceGeofenceRefresh),
            this.userAuthService.isAuthenticated$,
        ]).pipe(
            switchMap(([shouldForce, isAuthenticated]) => {
                if (!isAuthenticated) {
                    return of([] as Geofence[]);
                }
                if (shouldForce) {
                    return this.geofenceResourceService
                        .getGeofencesForUser()
                        .pipe(
                            switchMap((geofences) => {
                                this.store.dispatch(
                                    new SetGeofences(geofences)
                                );
                                this.store.dispatch(
                                    new ForceGeofenceRefresh(false)
                                );
                                return this.store.select(selectGeofences);
                            })
                        );
                } else {
                    return this.store.select(selectGeofences);
                }
            })
        );
    }

    getGeofenceById(id: string, forceRefresh = false): Observable<Geofence> {
        return this.getGeofences$().pipe(
            take(1),
            switchMap((geofences) => {
                const foundGeofence = geofences.find(
                    (geofence) => geofence.id === id
                );

                if (foundGeofence && !forceRefresh) {
                    return of(foundGeofence);
                }

                return this.getGeofenceFromResource(id);
            })
        );
    }

    //#endregion

    //#region Create
    createGeofence(geofence: Geofence): Observable<Geofence> {
        return this.geofenceResourceService
            .createGeofence(geofence.toContract())
            .pipe(
                switchMap((newGeofence) => {
                    this.store.dispatch(new CreateGeofence(newGeofence));
                    this.showOnMap(newGeofence);
                    return this.getGeofenceFromResource(newGeofence.id);
                })
            );
    }

    //#endregion

    //#region Update
    updateGeofence(geofence: Geofence): Observable<Geofence> {
        // let service set modification date
        geofence.modificationDate = undefined;
        const geofenceContract = geofence.toContract();
        return this.geofenceResourceService
            .updateGeofence(geofenceContract)
            .pipe(
                switchMap(() => this.getGeofenceById(geofence.id, true)),
                tap((newGeofence) => {
                    this.store.dispatch(new UpdateGeofence(newGeofence));
                })
            );
    }

    showOnMap(geofence: Geofence) {
        this.showOnMapService.setShouldShow(geofence.id, true);
        this.store.dispatch(new UpdateGeofence(geofence));
    }

    removeFromMap(geofence: Geofence) {
        this.showOnMapService.setShouldShow(geofence.id, false);
        this.store.dispatch(new UpdateGeofence(geofence));
    }

    //#endregion

    //#region Delete
    // both delete and deleteSelectedGeofences need to find the vehicles that are associated and update them.
    deleteGeofencesAndVehicleInfo(
        geofenceIds: string[]
    ): Observable<(false | { id: string; success: boolean })[]> {
        return this.webVehicleService.getVehiclesWithGeofences$().pipe(
            take(1),
            switchMap((vehicles) => {
                const foundVehicles = filter(vehicles, (vehicle) => {
                    return filter(
                        vehicle.geofences,
                        (limit: GeofenceSpeedLimit) =>
                            geofenceIds.includes(limit.geofenceId || '')
                    );
                }) as Vehicle[];

                if (!foundVehicles || foundVehicles.length === 0) {
                    return of([]);
                }

                foundVehicles.forEach((vehicle) => {
                    const modified = cloneDeep(vehicle.geofences);
                    const modifiedGeofences = remove(
                        modified,
                        (g: GeofenceSpeedLimit) => {
                            geofenceIds.includes(g.geofenceId || '');
                        }
                    );
                    vehicle.geofences = modifiedGeofences;
                    if (
                        geofenceIds.includes(
                            (vehicle.settings as VehicleSettingsContract)
                                .activeGeofence || ''
                        )
                    ) {
                        vehicle.settings ??= {};
                        vehicle.settings.activeGeofence = '';
                    }
                });

                const results = foundVehicles.map((v: Vehicle) => {
                    return this.webVehicleService.updateVehicle(v).pipe(
                        catchError(() => {
                            return of('failed');
                        }),
                        map((result) => {
                            return { id: v.id, success: result !== 'failed' };
                        })
                    );
                });

                return forkJoin(results);
            })
        );
    }

    deleteGeofence(id: string): Observable<void> {
        return this.deleteGeofenceAndVehicleInfo(id).pipe(
            switchMap((vehicleResult) => {
                if (!vehicleResult.success) {
                    return of(undefined);
                }
                return this.geofenceResourceService.deleteGeofence(id).pipe(
                    tap(() => {
                        this.store.dispatch(new DeleteGeofence(id));
                    })
                );
            })
        );
    }

    deleteSelectedGeofences(
        geofences: Geofence[]
    ): Observable<{ id: string; success: boolean }[]> {
        if (!geofences || geofences.length === 0) {
            return of([]);
        }

        const vehiclesUpdateResults = geofences.map((geofence) =>
            this.deleteGeofenceAndVehicleInfo(geofence.id)
        );

        return forkJoin(vehiclesUpdateResults).pipe(
            switchMap((vehicleResults) => {
                const deleteResults = vehicleResults.map((vehicleResult) => {
                    if (!vehicleResult.success) {
                        return of({
                            id: vehicleResult.geofenceId,
                            success: false,
                        });
                    }

                    return this.deleteGeofence(vehicleResult.geofenceId).pipe(
                        catchError(() => {
                            return of('failed');
                        }),
                        map((result) => {
                            return {
                                id: vehicleResult.geofenceId,
                                success: result !== 'failed',
                            };
                        })
                    );
                });

                if (!deleteResults || deleteResults.length === 0) {
                    return of([]);
                }

                return forkJoin(deleteResults);
            })
        );
    }

    //#endregion

    geofencesToGeoJson(geofences: Geofence[] = []): GeoJSON.FeatureCollection {
        const features = geofences.map((geofence) => geofence.toGeoJson());
        return new GeoJsonFeatureCollection(features);
    }

    private getGeofenceFromResource(id: string): Observable<Geofence> {
        return this.geofenceResourceService.getGeofenceById(id).pipe(
            tap((geofence) => {
                this.store.dispatch(new UpdateGeofence(geofence));
            })
        );
    }

    private deleteGeofenceAndVehicleInfo(
        geofenceId: string
    ): Observable<{ geofenceId: string; success: boolean }> {
        return this.webVehicleService.getVehiclesWithGeofences$().pipe(
            take(1),
            switchMap((vehicles) => {
                const foundVehicles = filter(vehicles, (v) => {
                    return filter(
                        v.geofences,
                        (g) => g.geofenceId === geofenceId
                    );
                }) as Vehicle[];

                if (!foundVehicles || foundVehicles.length === 0) {
                    return of([true]);
                }

                foundVehicles.forEach((v) => {
                    const modified = cloneDeep(v.geofences);
                    const modifiedGeofences = remove(
                        modified,
                        (g) => g.geofenceId === geofenceId
                    );
                    v.geofences = modifiedGeofences;
                    if (
                        v.settings &&
                        v.settings.activeGeofence === geofenceId
                    ) {
                        v.settings.activeGeofence = '';
                    }
                });

                const results = foundVehicles.map((v: Vehicle) => {
                    return this.webVehicleService.updateVehicle(v).pipe(
                        catchError(() => {
                            return of(false);
                        }),
                        map((result) => {
                            return result !== false;
                        })
                    );
                });

                return forkJoin(results);
            }),
            map((results) => ({
                geofenceId,
                success: every(results),
            }))
        );
    }
}
